import React, { Component } from 'react';
import Cookies from 'js-cookie';
import apiConfig from '../config/config';
import RestClient from '../service/rest-client';

class AuthWrapper extends Component {
  state = {
    isAuthenticated: false,
    isLoading: true,
    username: '',
    password: '',
    error: '',
    sessionExpired: false
  };

  componentDidMount() {
    this.checkAuthentication();
    
    // Set up periodic token validation
    this.tokenCheckInterval = setInterval(() => {
      this.validateToken();
    }, 30000); // Check every 30 seconds
  }

  componentWillUnmount() {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
    }
  }

  checkAuthentication = () => {
    // Check if we have a token in the URL (from redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (token) {
      // Save token and remove from URL
      Cookies.set('management_auth_token', token, { expires: 7 });
      window.history.replaceState({}, document.title, window.location.pathname);
      this.validateToken(token);
    } else {
      // Check existing cookie
      const existingToken = Cookies.get('management_auth_token');
      if (existingToken) {
        this.validateToken(existingToken);
      } else {
        this.setState({ isLoading: false });
      }
    }
  };

  validateToken = async (token = null) => {
    const authToken = token || Cookies.get('management_auth_token');
    
    if (!authToken) {
      this.handleSessionExpired();
      return;
    }

    try {
      // Test the token by making a simple API call
      const response = await fetch(`${apiConfig.STACKRY_API_SERVER}/oauth2/user`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'api-key': apiConfig.STACKRY_API_KEY
        },
      });

      if (response.ok) {
        const userData = await response.json();
        
        // Update account info if needed
        if (userData.accountId) {
          Cookies.set('management_account_id', userData.accountId, { expires: 7 });
        }
        
        // Reset REST client auth state when token is valid
        RestClient.resetAuthState();
        
        this.setState({ 
          isAuthenticated: true, 
          isLoading: false, 
          sessionExpired: false,
          error: '' 
        });
      } else if (response.status === 401) {
        // Token is invalid/expired
        this.handleSessionExpired();
      } else {
        // Other error, but don't log out immediately
        console.warn('Token validation failed with status:', response.status);
        this.setState({ isLoading: false });
      }
    } catch (error) {
      console.error('Token validation error:', error);
      // Network error - don't immediately log out, but set loading to false
      this.setState({ isLoading: false });
    }
  };

  handleSessionExpired = () => {
    // Clear tokens
    Cookies.remove('management_auth_token');
    Cookies.remove('management_account_id');
    
    this.setState({ 
      isAuthenticated: false, 
      isLoading: false,
      sessionExpired: true,
      error: 'Your session has expired. Please log in again.'
    });
  };

  handleLogin = async (e) => {
    e.preventDefault();
    const { username, password } = this.state;
    
    if (!username || !password) {
      this.setState({ error: 'Please enter both username and password' });
      return;
    }

    this.setState({ isLoading: true, error: '' });

    try {
      // Create form data for the request
      const formData = new URLSearchParams();
      formData.append('grant_type', 'password');
      formData.append('username', username);
      formData.append('password', password);
      formData.append('client_id', apiConfig.STACKRY_API_KEY);

      // Call the login API using the correct endpoint
      const response = await fetch(`${apiConfig.STACKRY_API_SERVER}/oauth2/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      const data = await response.json();
      
      if (response.ok && data.access_token) {
        // Get user info to get accountId
        const userResponse = await fetch(`${apiConfig.STACKRY_API_SERVER}/oauth2/user`, {
          headers: {
            'Authorization': `Bearer ${data.access_token}`,
          },
        });
        
        const userData = await userResponse.json();
        
        // Save token and account info
        Cookies.set('management_auth_token', data.access_token, { expires: 7 });
        Cookies.set('management_account_id', userData.accountId || '', { expires: 7 });
        
        // Reset REST client auth state when user successfully logs in
        RestClient.resetAuthState();
        
        this.setState({ 
          isAuthenticated: true, 
          isLoading: false,
          sessionExpired: false,
          error: '',
          username: '',
          password: ''
        });
      } else {
        this.setState({ 
          error: data.error_description || 'Invalid username or password',
          isLoading: false
        });
      }
    } catch (err) {
      console.error('Login error:', err);
      this.setState({ 
        error: 'Failed to login. Please try again.',
        isLoading: false
      });
    }
  };

  handleLogout = () => {
    Cookies.remove('management_auth_token');
    Cookies.remove('management_account_id');
    this.setState({ 
      isAuthenticated: false, 
      username: '', 
      password: '', 
      sessionExpired: false,
      error: ''
    });
  };

  render() {
    const { isAuthenticated, isLoading, username, password, error, sessionExpired } = this.state;

    if (isLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', marginBottom: '10px' }}>Loading...</div>
            <div style={{ fontSize: '14px', color: '#666' }}>Validating your session</div>
          </div>
        </div>
      );
    }

    if (!isAuthenticated) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          backgroundColor: '#f5f5f5'
        }}>
          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            width: '400px'
          }}>
            <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>Stackry Reporting Login</h2>
            
            {sessionExpired && (
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
                color: '#856404',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '20px',
                fontSize: '14px'
              }}>
                <strong>Session Expired:</strong> Your login session has expired for security reasons. Please log in again to continue.
              </div>
            )}
            
            <form onSubmit={this.handleLogin}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>
                  Username:
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => this.setState({ username: e.target.value, error: '' })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                  placeholder="Enter your username"
                  autoComplete="username"
                  disabled={isLoading}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>
                  Password:
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => this.setState({ password: e.target.value, error: '' })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  disabled={isLoading}
                />
              </div>
              {error && (
                <div style={{ 
                  color: '#dc3545', 
                  backgroundColor: '#f8d7da',
                  border: '1px solid #f5c6cb',
                  padding: '12px',
                  borderRadius: '4px',
                  marginBottom: '20px',
                  fontSize: '14px'
                }}>
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  backgroundColor: isLoading ? '#ccc' : '#36c6d3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {isLoading ? 'Signing In...' : 'Login'}
              </button>
            </form>
            <div style={{ marginTop: '20px', fontSize: '14px', color: '#666', textAlign: 'center' }}>
              Use your Stackry management account credentials to access reporting.
            </div>
          </div>
        </div>
      );
    }

    return (
      <div>
        {this.props.children}
      </div>
    );
  }
}

export default AuthWrapper; 